import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Gavel, RefreshCw, Check, X, MinusCircle, Loader2, AlertTriangle, Eye } from 'lucide-react';
import { toast } from 'sonner';
import TicketDetailModal from './TicketDetailModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const oddsStr = (o) => (o > 0 ? `+${o}` : `${o}`);
const decimal = (american) => (american > 0 ? american / 100 + 1 : 100 / Math.abs(american) + 1);

const GRADES = [
    { key: 'won', label: 'Win', icon: Check, cls: 'bg-[#39FF14]/15 text-[#39FF14] border-[#39FF14]/40' },
    { key: 'lost', label: 'Loss', icon: X, cls: 'bg-[#FF3B30]/15 text-[#FF6B6B] border-[#FF3B30]/40' },
    { key: 'void', label: 'Void', icon: MinusCircle, cls: 'bg-white/10 text-[#A0AAB2] border-white/20' },
];

/**
 * ManualGradePanel — grade prop / ungradeable bets by hand. RULE: nothing is
 * credited and no win is recorded until "Finalize & Settle" is pressed. Draft
 * grades persist but never move a balance. Reads/writes
 * GET|POST /api/admin/sports/manual-grade.
 */
export default function ManualGradePanel({ onChanged } = {}) {
    const [bets, setBets] = useState(null);
    const [loading, setLoading] = useState(false);
    const [draft, setDraft] = useState({});      // { betId: { legIndex: grade } }
    const [busy, setBusy] = useState(null);       // betId currently saving/finalizing
    const [viewBet, setViewBet] = useState(null); // ticket shown in the full-view modal

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/sports/manual-grade/pending`, { withCredentials: true });
            setBets(data.bets || []);
            // seed drafts from any grades already stored
            const seed = {};
            (data.bets || []).forEach((b) => {
                seed[b.id] = {};
                b.legs.forEach((l) => { seed[b.id][l.index] = l.grade && l.grade !== 'pending' ? l.grade : null; });
            });
            setDraft(seed);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load bets to grade');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const setLeg = (betId, idx, grade) =>
        setDraft((p) => ({ ...p, [betId]: { ...(p[betId] || {}), [idx]: grade } }));

    const payloadFor = (b) => ({
        grades: b.legs.map((l) => ({ index: l.index, grade: draft[b.id]?.[l.index] || 'pending' })),
    });

    // Live payout preview using current draft grades (matches backend math).
    const preview = (b) => {
        const g = draft[b.id] || {};
        const graded = b.legs.map((l) => g[l.index]);
        if (graded.some((x) => !x)) return { label: 'Grade all legs to see result', result: null };
        if (graded.some((x) => x === 'lost')) return { label: 'LOSS — no payout', result: 'lost', payout: 0 };
        const wonLegs = b.legs.filter((l) => g[l.index] === 'won');
        if (wonLegs.length === 0) return { label: `VOID — refund ${fmt(b.amount)}`, result: 'void', payout: b.amount };
        if (b.type === 'parlay') {
            const dec = wonLegs.reduce((acc, l) => acc * decimal(l.odds), 1);
            const payout = Math.round(b.amount * dec * 100) / 100;
            return { label: `WIN — pays ${fmt(payout)}`, result: 'won', payout };
        }
        return { label: `WIN — pays ${fmt(b.potential_payout)}`, result: 'won', payout: b.potential_payout };
    };

    const saveDraft = async (b) => {
        setBusy(b.id);
        try {
            await axios.post(`${API}/admin/sports/manual-grade/${b.id}`, { ...payloadFor(b), finalize: false }, { withCredentials: true });
            toast.success('Draft saved — no balance changed');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        } finally { setBusy(null); }
    };

    const finalize = async (b) => {
        const g = draft[b.id] || {};
        if (b.legs.some((l) => !g[l.index])) { toast.error('Grade every leg before finalizing'); return; }
        const pv = preview(b);
        if (!window.confirm(`Finalize this ticket?\n\nResult: ${pv.label}\n\nThis will settle it${pv.payout > 0 ? ` and credit ${fmt(pv.payout)}` : ' with no payout'}.`)) return;
        setBusy(b.id);
        try {
            const { data } = await axios.post(`${API}/admin/sports/manual-grade/${b.id}`, { ...payloadFor(b), finalize: true }, { withCredentials: true });
            toast.success(`Settled: ${data.status.toUpperCase()}${data.payout > 0 ? ` · paid ${fmt(data.payout)}` : ''}`);
            load();
            onChanged && onChanged();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Finalize failed');
            load();
        } finally { setBusy(null); }
    };

    return (
        <div className="card-dark p-5 space-y-4" data-testid="manual-grade-panel">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Gavel className="w-5 h-5 text-[#FFD700]" />
                    <h3 className="text-sm uppercase font-bold tracking-wide text-white">Manual bet grading — props</h3>
                    <span className="text-[10px] text-[#A0AAB2]">nothing pays until you finalize</span>
                </div>
                <button onClick={load} disabled={loading}
                    className="text-xs text-[#A0AAB2] hover:text-white flex items-center gap-1" data-testid="manual-grade-refresh">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />Refresh
                </button>
            </div>

            <div className="flex items-start gap-2 text-[11px] text-[#FFD700] bg-[#FFD700]/5 border border-[#FFD700]/20 rounded-lg p-2.5">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Player props can't be auto-graded (no stats feed yet), so these tickets wait here. Grade each leg, then <b>Finalize &amp; Settle</b> — only then is a balance credited. Saving a draft never pays anyone.</span>
            </div>

            {!bets ? (
                <p className="text-[11px] text-[#A0AAB2] italic">Loading…</p>
            ) : bets.length === 0 ? (
                <div className="rounded-lg border border-white/5 bg-[#0a0a0a] p-4 text-center text-[11px] text-[#A0AAB2] italic" data-testid="manual-grade-empty">
                    No tickets awaiting manual grading. 🎉
                </div>
            ) : bets.map((b) => {
                const pv = preview(b);
                const g = draft[b.id] || {};
                return (
                    <div key={b.id} className="rounded-lg border border-white/10 bg-[#0e0e0e] p-3 space-y-2" data-testid={`grade-bet-${b.id}`}>
                        <div className="flex items-center justify-between text-xs">
                            <span className="text-white font-bold">{b.name}
                                <span className="ml-2 text-[9px] uppercase px-1.5 py-0.5 rounded-full bg-white/10 text-[#A0AAB2]">{b.type}</span>
                            </span>
                            <div className="flex items-center gap-2">
                                <button type="button" onClick={() => setViewBet(b)}
                                    className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-md border border-[#FFD700]/30 text-[#FFD700] hover:bg-[#FFD700]/10 transition-colors"
                                    data-testid={`grade-view-${b.id}`}>
                                    <Eye className="w-3 h-3" />View ticket
                                </button>
                                <span className="font-mono text-[#A0AAB2]">stake {fmt(b.amount)} · to win {fmt(b.potential_payout)}</span>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            {b.legs.map((l) => (
                                <div key={l.index} className="flex items-center justify-between gap-2 bg-black/30 rounded-md px-2.5 py-1.5" data-testid={`grade-leg-${b.id}-${l.index}`}>
                                    <div className="min-w-0">
                                        <p className="text-[11px] text-white truncate">{l.description && !((l.outcome_name || '').toLowerCase().startsWith(l.description.toLowerCase())) ? `${l.description} ` : ''}{l.outcome_name}
                                            <span className="text-[#666] font-mono"> · {oddsStr(l.odds)}</span>
                                        </p>
                                        <p className="text-[10px] text-[#A0AAB2] truncate">{l.market_label}{l.away_team ? ` · ${l.away_team} @ ${l.home_team}` : ''}</p>
                                    </div>
                                    <div className="flex gap-1 shrink-0">
                                        {GRADES.map((gr) => {
                                            const active = g[l.index] === gr.key;
                                            const Icon = gr.icon;
                                            return (
                                                <button key={gr.key} type="button"
                                                    onClick={() => setLeg(b.id, l.index, gr.key)}
                                                    className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-md border transition-colors ${active ? gr.cls : 'border-white/10 text-[#666] hover:text-white hover:border-white/30'}`}
                                                    data-testid={`grade-leg-${b.id}-${l.index}-${gr.key}`}>
                                                    <Icon className="w-3 h-3" />{gr.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex items-center justify-between gap-2 pt-1">
                            <span className={`text-[11px] font-bold ${pv.result === 'won' ? 'text-[#39FF14]' : pv.result === 'lost' ? 'text-[#FF6B6B]' : pv.result === 'void' ? 'text-[#A0AAB2]' : 'text-[#666]'}`} data-testid={`grade-preview-${b.id}`}>
                                {pv.label}
                            </span>
                            <div className="flex gap-2">
                                <button type="button" onClick={() => saveDraft(b)} disabled={busy === b.id}
                                    className="text-[11px] px-3 py-1.5 rounded-md border border-white/15 text-[#A0AAB2] hover:text-white hover:border-white/30 disabled:opacity-50"
                                    data-testid={`grade-save-${b.id}`}>
                                    {busy === b.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save draft'}
                                </button>
                                <button type="button" onClick={() => finalize(b)} disabled={busy === b.id || pv.result === null}
                                    className="text-[11px] px-3 py-1.5 rounded-md bg-[#FFD700] text-black font-bold hover:bg-[#e6c200] disabled:opacity-40 disabled:cursor-not-allowed"
                                    data-testid={`grade-finalize-${b.id}`}>
                                    Finalize &amp; Settle
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })}
            <TicketDetailModal bet={viewBet} onClose={() => setViewBet(null)} />
        </div>
    );
}
